import { useState } from "react";
import { CreditCard, Landmark, FileCheck2 } from "lucide-react";
import { TextInput, MaskedInput, ButtonGroupInput, FileUpload } from "../components/fields";
import { Card } from "../components/Card";
import { Alert } from "../components/Toast";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { getApi } from "../api";
import { trackWarning } from "../telemetry/appInsights";
import { saveCurrentDraft } from "../services/saveDraft";
import type { useStepValidation } from "../hooks";

interface StepPaymentProps {
  validation: ReturnType<typeof useStepValidation>;
}

const PAYMENT_METHOD_OPTIONS = ["ACH", "Check"];
const ACCOUNT_TYPE_OPTIONS = ["Checking", "Savings"];

// Story 3352 spec restricts the binder check upload to image + PDF only;
// the shared FileUpload component otherwise accepts xlsx/csv for census docs.
const BINDER_CHECK_EXTENSIONS = ["pdf", "jpg", "jpeg", "png"];

export function StepPayment({ validation }: StepPaymentProps) {
  const paymentMethod = useFormStore((s) => s.data.paymentMethod);
  const binderCheck = useFormStore((s) => s.data.documents.binderCheck);
  // Renewals don't bind new coverage, so no binder check exists to attach
  // (Mike Bentz, 2026-08-12) — the upload stays available but optional.
  const isRenewal = useFormStore((s) => s.meta.isRenewal);
  const setField = useFormStore((s) => s.setField);
  const setDocument = useFormStore((s) => s.setDocument);

  // Touch counter — same pattern as StepStructure / StepDocuments. Captures
  // the value at mount so toasts fired on previous steps don't immediately
  // flash an error on the binder-check upload before the user has touched it.
  const globalTouchCounter = useUiStore((s) => s.validationTouchCounter);
  const [mountCounter] = useState(globalTouchCounter);
  const touchAll = Math.max(0, globalTouchCounter - mountCounter);

  // Switching payment method clears the fields of the now-hidden section so
  // the user can't accumulate stale ACH/check data behind the scenes. Mirrors
  // StepContacts's fiduciary toggle and StepDocuments's docVerificationType
  // cleanup — when switching away from Check, fire-and-forget the binder-check
  // blob delete so we don't orphan storage. The ref is cleared regardless.
  const handlePaymentMethodChange = (value: string) => {
    if (value === "Check") {
      setField("achAccountHolderName", "");
      setField("achBankName", "");
      setField("achRoutingNumber", "");
      setField("achAccountNumber", "");
      setField("achAccountType", "Checking");
      validation.clearErrors([
        "achAccountHolderName",
        "achBankName",
        "achRoutingNumber",
        "achAccountNumber",
        "achAccountType",
      ]);
    } else if (value === "ACH") {
      const current = useFormStore.getState().data.documents.binderCheck;
      if (current) {
        const api = getApi();
        void api.deleteFile(current.fileId).catch(() =>
          trackWarning("StepPayment: binder check cleanup delete failed", { fileId: current.fileId }),
        );
      }
      setDocument("binderCheck", null);
      validation.clearErrors(["documents.binderCheck"]);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Payment Method</h1>
      <p className="text-gray-500 mb-6">How will you remit premium payments?</p>

      <Card title="Payment Method" icon={CreditCard}>
        <ButtonGroupInput
          field="paymentMethod"
          label="Payment Method"
          hint="You may pay by business check or set up automatic bank withdrawal (ACH)."
          options={PAYMENT_METHOD_OPTIONS}
          validation={validation}
          required
          noPrefill
          onChange={handlePaymentMethodChange}
        />
      </Card>

      {paymentMethod === "ACH" && (
        <div className="section-reveal">
          <Card title="ACH Information" icon={Landmark}>
            {/* One field per row — the "9 digits" hint above the routing
                number throws off side-by-side alignment, so we stack. */}
            <div className="mb-4">
              <TextInput
                field="achAccountHolderName"
                label="Account Holder Name"
                validation={validation}
                placeholder="Full name on account"
                maxLength={120}
                noPrefill
                required
              />
            </div>
            <div className="mb-4">
              <TextInput
                field="achBankName"
                label="Bank Name"
                validation={validation}
                placeholder="Name of your bank"
                maxLength={80}
                noPrefill
                required
              />
            </div>
            <div className="mb-4">
              <MaskedInput
                field="achRoutingNumber"
                label="ACH Routing (ABA) Number"
                mask="routing"
                hint="9 digits"
                validation={validation}
                noPrefill
                required
              />
            </div>
            <div className="mb-4">
              <MaskedInput
                field="achAccountNumber"
                label="Account Number"
                mask="account"
                validation={validation}
                noPrefill
                required
              />
            </div>
            <ButtonGroupInput
              field="achAccountType"
              label="Account Type"
              options={ACCOUNT_TYPE_OPTIONS}
              validation={validation}
              noPrefill
              required
            />
          </Card>
        </div>
      )}

      {paymentMethod === "Check" && (
        <div className="section-reveal">
          <Card title="Binder Check" icon={FileCheck2}>
            <Alert
              type="info"
              showIcon={false}
              message={
                <>
                  When paying by check, please include a copy of the check and mail it within 3
                  business days of submission, made out to:{" "}
                  <strong>
                    Allied Benefit Systems, LLC, P.O. Box 2551, Carol Stream, IL 60132-2551
                  </strong>
                </>
              }
              className="mb-4"
            />
            <FileUpload
              docType="binderCheck"
              label="Binder Check"
              currentFile={binderCheck}
              onUpload={(ref) => {
                setDocument("binderCheck", ref);
                // Persist paymentMethod=Check immediately so a reload before the user
                // navigates doesn't desync the form (ACH shown, but binder file in SF).
                void saveCurrentDraft();
              }}
              onDelete={() => setDocument("binderCheck", null)}
              required={!isRenewal}
              error={validation.getFieldError("documents.binderCheck")}
              touchAllCounter={touchAll}
              acceptExtensions={BINDER_CHECK_EXTENSIONS}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
